/**
 * Fila plegable de un árbol de montos (09/10/2026): etiqueta + descripción +
 * monto + % del total, con hijas que se despliegan con un chevron. Nació para
 * el árbol de gasto del tab Bedrock de Observability; PnlDrivers (tornado),
 * CompanyMetricsDashboard (filas hijas) y finops/AlertRow tenían cada uno su
 * `useState<Set>` + chevron a mano, atados a su grid o a su tabla — este es el
 * genérico para no escribir una cuarta copia.
 */
import { Children, useId, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TreeRowProps {
  label: ReactNode;
  description?: ReactNode;
  amount: ReactNode;
  /** Texto ya formateado ("12,3 %"); null/undefined deja la columna vacía. */
  pct?: string | null;
  /** Dato chico a la derecha de la etiqueta (p. ej. "40 llamadas · 6 personas"). */
  meta?: ReactNode;
  depth?: 0 | 1 | 2 | 3;
  defaultOpen?: boolean;
  /** Fila principal (total, bloque): negrita y monto más grande. */
  emphasis?: boolean;
  className?: string;
  children?: ReactNode;
}

export function TreeRow({ label, description, amount, pct, meta, depth = 0, defaultOpen = false, emphasis, className, children }: TreeRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  // `[]` o `[null, null]` (un .map() sin filas) no es "tiene hijas": sin esto el chevron abría un panel vacío.
  const canOpen = Children.toArray(children).length > 0;
  const title = typeof label === 'string' ? label : undefined;
  const head = (
    <>
      {canOpen ? (
        <ChevronRight aria-hidden className={cn('h-4 w-4 flex-none text-muted-foreground transition-transform', open && 'rotate-90')} />
      ) : (
        <span className="h-4 w-4 flex-none" />
      )}
      <div className="min-w-0 flex-1 text-left">
        <div className={cn('truncate', emphasis ? 'text-sm font-semibold' : 'text-sm')} title={title}>{label}</div>
        {description && <div className="truncate text-xs text-muted-foreground">{description}</div>}
      </div>
    </>
  );
  return (
    <div className={cn('border-b last:border-0', className)}>
      <div className="flex items-center gap-2 py-2 pr-3" style={{ paddingLeft: 12 + depth * 20 }}>
        {canOpen ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={panelId}
            className="flex min-w-0 flex-1 items-center gap-2 rounded hover:bg-muted/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          >
            {head}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">{head}</div>
        )}
        {meta && <div className="hidden text-xs text-muted-foreground sm:block">{meta}</div>}
        <div className={cn('w-24 text-right tabular-nums', emphasis ? 'text-base font-semibold' : 'text-sm font-medium')}>{amount}</div>
        <div className="w-14 text-right text-xs tabular-nums text-muted-foreground">{pct ?? ''}</div>
      </div>
      {canOpen && open && <div id={panelId} className="bg-muted/10">{children}</div>}
    </div>
  );
}
